import axios from 'axios'
import type { AxiosInstance } from 'axios'
import { config } from '@/config'

const API_HOST = config.api.baseUrl

const apiInstance: AxiosInstance = axios.create({
  baseURL: API_HOST,
  // withCredentials: true,
  timeout: 30000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token. Admin and reception sessions are stored
// under separate localStorage keys so both can be logged in independently in the
// same browser — which pair applies is decided by the current path's "zone".
apiInstance.interceptors.request.use((config) => {
  const isRcp = window.location.pathname.startsWith('/rcp/')
  const token = localStorage.getItem(isRcp ? 'rcpAuthToken' : 'authToken')

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

// Response interceptor for error handling
apiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      console.error('🔴 API Error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.response.config?.url,
      })
    }

    if (error.response?.status === 401) {
      const currentPath = window.location.pathname
      const isRcp = currentPath.startsWith('/rcp/')

      if (currentPath.startsWith('/rcp/login') || currentPath === '/login') {
        return Promise.reject(error)
      }

      if (isRcp) {
        localStorage.removeItem('rcpAuthToken')
        localStorage.removeItem('rcpUser')
        localStorage.removeItem('rcpIsAuthenticated')
        localStorage.removeItem('rcpSessionFromSignup')
        window.location.href = '/login'
        return Promise.reject(error)
      }

      const adminToken = localStorage.getItem('adminAuthToken')
      if (adminToken) {
        // Impersonation token expired/rejected — silently stop impersonation and
        // restore the Super Admin's own session without forcing a login page visit.
        localStorage.removeItem('adminAuthToken')
        localStorage.setItem('authToken', adminToken)
        // Let the auth store re-hydrate on the next navigation; hard-reload to
        // ensure all components pick up the restored admin token cleanly.
        window.location.href = '/'
      } else {
        localStorage.removeItem('authToken')
        localStorage.removeItem('user')
        localStorage.removeItem('isAuthenticated')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export default apiInstance
export { API_HOST }
